import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {takeUntil} from "rxjs/operators";
import {ChartDataService} from "../../services/chart-data/chart-data.service";
import {ChartFilters} from "../../models/chart-filters.model";
import {Subject} from "rxjs";
import {IKpiLine} from "../../models/kpi.model";
import {ChartVidVehiclePage} from "../../models/chart-vid-vehicle-page.model";
import {IVidVehicle} from "../../models/chart-vid-vehicle.model";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {DatePipe, NgClass} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {LoaderComponent} from "../../shared/components/loader/loader.component";
import {MatPaginator} from "@angular/material/paginator";
import {MatTooltip} from "@angular/material/tooltip";
import {MatTableModule} from "@angular/material/table";
import {MatDialogContent, MatDialogTitle, MatDialogActions, MatDialogClose} from "@angular/material/dialog";
import {MatButton} from "@angular/material/button";
import {MatTableResponsiveDirective} from "../../shared/directives/mat-table-responsive/mat-table-responsive.directive";

@Component({
  selector: 'colimo-vid-vehicle-dialog',
  templateUrl: './vid-vehicle-dialog.component.html',
  styleUrls: ['./vid-vehicle-dialog.component.scss'],
  imports: [
    NgClass,
    TranslateModule,
    MatIcon,
    DatePipe,
    LoaderComponent,
    MatPaginator,
    MatTooltip,
    MatTableModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatButton,
    MatTableResponsiveDirective
  ],
  /*standalone: true,
  imports: [SharedModule, MatTableModule, MatPaginatorModule, TranslateModule, MatIconModule, DatePipe, MatTooltipModule, MatDialogModule, MatButtonModule, NgClass]*/
})
export class VidVehicleDialogComponent implements OnInit, OnDestroy {

  public displayedColumns: string[] = ['carStatus', 'vehicleId', 'redComps', 'model', 'line', 'measurementDate'];
  public pageSizeOptions: number[] = [10, 20, 50];
  public pageSize = 50;
  public currentPage = 0;
  public totalSize = 0;
  public isLoading: boolean;
  public title: string;
  public vidVehiclePage: ChartVidVehiclePage;

  private stop$: Subject<boolean> = new Subject();

  constructor(
    private readonly chartDataService: ChartDataService,
    private readonly translateService: TranslateService,
    private readonly router: Router,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      chartFilters: ChartFilters;
      startDateFormatted: string,
      endDateFormatted: string,
      kpiLine: IKpiLine,
      minCarStatus: number,
      maxCarStatus: number,
      componentTranslations: string[],
      productType: string
    }) {}

  public ngOnInit(): void {
    this.isLoading = false;
    this.title = (this.data.productType ? this.data.productType : this.translateService.instant('dashboard.vidPlotTitle')) + ' - ' + this.data.kpiLine.site + ' - ' + this.data.kpiLine.lineNumber;
    this.loadVidVehicles();
  }

  public ngOnDestroy(): void {
    this.stop$.next(true);
    this.stop$.unsubscribe();
  }

  public pageEvent(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadVidVehicles();
  }

  private evaluateCarStatusCss(res: ChartVidVehiclePage): ChartVidVehiclePage {
    for (let i = 0; i < res.content.length; i++) {
      if (res.content[i].redComps && res.content[i].redComps !== '') {
        res.content[i].redComps = this.translateComponents(res.content[i].redComps);
      }
      switch (res.content[i].carStatus) {
        case 0:
          res.content[i].carStatusCss = 'green-cell';
          break;
        case 1:
          res.content[i].carStatusCss = 'yellow-cell';
          break;
        default:
          res.content[i].carStatusCss = 'red-cell';
          break;
      }
    }
    return res;
  }

  private translateComponents(components: string): string {
    if (this.data.componentTranslations) {
      // transform to array and eliminate duplicates
      const compArray: string[] = Array.from(new Set(components.split(',').filter(Boolean)));
      let translatedComps: string = '';
      for (let i = 0; i < compArray.length; i++) {
        translatedComps += this.data.componentTranslations[compArray[i]] ? this.data.componentTranslations[compArray[i]] : compArray[i];
        translatedComps += ', ';
      }
      return translatedComps.replace(/, $|,$/, '');
    }

    // return unchanged if we dont have translations
    return components;
  }

  public showCarReport(vehicle: IVidVehicle): void {
    // ==> open car report in new tab if lineType is BODY -- lineType evaluation because of average lines which e.g. in case of Tesla can contain BODY and BUMPER lines
    if (!(vehicle.lineType === 'B')) {
      return;
    }

    const extras = {
      vehicleId: vehicle.vehicleId,
      lstaNr: vehicle.lstaNr
    } as any;

    // const queryParams = new URLSearchParams(extras).toString();
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/car-report'], { queryParams: extras })
    );

    // open car report in new tab
    window.open('/#' + url, '_blank');
  }

  private loadVidVehicles(): void {
    this.isLoading = true;
    this.chartDataService.getVidVehicles(this.data.chartFilters, this.data.startDateFormatted, this.data.endDateFormatted,
      this.currentPage, this.pageSize, this.data.minCarStatus, this.data.maxCarStatus).pipe(takeUntil(this.stop$)).subscribe({
      next: (response: ChartVidVehiclePage) => {
        // ==> populate table with results
        this.vidVehiclePage = this.evaluateCarStatusCss(response);
        this.totalSize = response.page.totalElements;
        this.isLoading = false;
      },
      error: () => {
        // handle error
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    })
  }
}
